import * as Yup from 'yup';
import {
	SUB_BILLING_PER_ORDER,
	PER_ORDER_MIN_ROWS,
	PER_ORDER_MAX_ROWS,
	makeDefaultRateRows,
} from './PerOrderRatesField';

// Billing Type Constants
export const BILLING_TYPE_FIXED_VARIABLE = 'Fixed + Variable';
export const BILLING_TYPE_FIXED = 'Fixed';
export const BILLING_TYPE_VARIABLE = 'Variable';
export const BILLING_TYPE_FIXED_PER_DAY = 'Fixed Per Day';

// Temperature Controlled Options
export const temperatureOptions = [
	{ value: true, label: 'Yes' },
	{ value: false, label: 'No' },
];

// Billing Type Options
export const billingTypeOptions = [
	{ value: BILLING_TYPE_VARIABLE, label: 'Variable' },
	{ value: BILLING_TYPE_FIXED_VARIABLE, label: 'Fixed + Variable' },
	{ value: BILLING_TYPE_FIXED_PER_DAY, label: 'Fixed Rate Per Day' },
];

// Week Off Days
export const WEEK_OFF_DAYS = [
	'Sunday',
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
];

// Week Off Days Options
export const weekOffDaysOptions = WEEK_OFF_DAYS.map((day) => ({
	value: day,
	label: day,
}));

// Helper to get base form values
const getBaseValues = (formData, isEditMode) => {
	// Convert monthly_off from string to array for multi-select dropdown
	let monthlyOffArray = [];
	if (formData?.monthly_off) {
		if (Array.isArray(formData.monthly_off)) {
			monthlyOffArray = formData.monthly_off;
		} else if (typeof formData.monthly_off === 'string') {
			// If backend sends comma-separated string, convert to array
			monthlyOffArray = formData.monthly_off.split(',').map((day) => day.trim());
		}
	}

	return {
		...(isEditMode && { id: formData?.id }),
		client_id: formData?.client_id || undefined,
		temperature_controlled: formData?.temperature_controlled ?? undefined,
		billing_type: formData?.billing_type || undefined,
		project_id: formData?.project_id || undefined,
		vehicle_type_id: formData?.vehicle_type_id || undefined,
		capacity_tonnes: formData?.capacity_tonnes || '',
		contract_date: formData?.contract_date || undefined,
		expiry_date: formData?.expiry_date || undefined,
		monthly_off: monthlyOffArray,
		route_id: formData?.route_id || undefined,
	};
};

// Helper to get cost-related values
const getCostValues = (formData) => ({
	vehicle_cost: formData?.vehicle_cost ?? '',
	loader_cost: formData?.loader_cost ?? '',
	delivery_associate_cost: formData?.delivery_associate_cost ?? '',
});

// Helper to get billing-specific values
const getBillingSpecificValues = (formData) => ({
	// Fixed + Variable and Fixed fields
	fixed_hours_per_day: formData?.fixed_hours_per_day ?? '',
	threshold_km_per_day: formData?.threshold_km_per_day ?? '',
	threshold_km_per_month: formData?.threshold_km_per_month ?? '',
	// Backend sends other_expenses_value, map to form field other_other_expenses
	other_other_expenses: formData?.other_expenses_value ?? formData?.other_other_expenses ?? '',
	// Backend sends other_expenses_type, map to form field other_expenses
	other_expenses: formData?.other_expenses_type || formData?.other_expenses || '', // Text field for "Other Expenses"
	// Fixed + Variable specific
	extra_charges_per_trip: formData?.extra_charges_per_trip ?? '',
	extra_charges_per_hour: formData?.extra_charges_per_hour ?? '',
	// Variable specific
	charges_per_km: formData?.charges_per_km ?? '',
	// Fixed Per Day specific
	fixed_rate_per_day: formData?.fixed_rate_per_day ?? '',
	billing_subtype: formData?.billing_subtype || undefined,
	per_order_rates: (() => {
		const rates = Array.isArray(formData?.order_rates)
			? formData.order_rates
			: formData?.per_order_rates;
		return Array.isArray(rates) && rates.length ? rates : makeDefaultRateRows();
	})(),
});

// Initial Form Values Generator
export const getInitialValues = (formData = {}, isEditMode = false) => ({
	...getBaseValues(formData, isEditMode),
	...getCostValues(formData),
	...getBillingSpecificValues(formData),
});

// Validation Schema based on API contract
export const FormSchema = Yup.object({
	client_id: Yup.mixed()
		.test('client-id-required', 'Client is required', (value) => {
			if (value === undefined || value === null) return false;
			if (typeof value === 'string') return value.trim() !== '';
			return true;
		})
		.label('Client'),
	project_id: Yup.number()
		.required('Project is required')
		.integer('Project ID must be an integer')
		.label('Project'),
	billing_type: Yup.string()
		.required('Billing type is required')
		.oneOf(
			[
				BILLING_TYPE_FIXED,
				BILLING_TYPE_VARIABLE,
				BILLING_TYPE_FIXED_VARIABLE,
				BILLING_TYPE_FIXED_PER_DAY,
			],
			'Invalid billing type'
		)
		.label('Billing Type'),
	temperature_controlled: Yup.boolean()
		.required('Temperature controlled is required')
		.label('Temperature Controlled'),
	vehicle_type_id: Yup.number()
		.required('Vehicle type is required')
		.integer('Vehicle type ID must be an integer')
		.label('Vehicle Type'),
	capacity_tonnes: Yup.number()
		.required('Capacity is required')
		.min(0.01, 'Capacity must be at least 0.01')
		.max(999.99, 'Capacity must not exceed 999.99')
		.label('Capacity'),
	contract_date: Yup.date().required('Contract date is required').label('Contract Date'),
	expiry_date: Yup.date()
		.required('Expiry date is required')
		.min(Yup.ref('contract_date'), 'Expiry date must be after contract date')
		.label('Expiry Date'),
	route_id: Yup.number()
		.required('Route is required')
		.integer('Route ID must be an integer')
		.label('Route Name'),
	monthly_off: Yup.array()
		.of(Yup.string().oneOf(WEEK_OFF_DAYS, 'Invalid week off day'))
		.nullable()
		.label('Weekly Off'),
	// Fixed + Variable and Fixed specific fields
	fixed_hours_per_day: Yup.number()
		.when('billing_type', {
			is: (val) =>
				val === BILLING_TYPE_FIXED ||
				val === BILLING_TYPE_FIXED_VARIABLE ||
				val === BILLING_TYPE_FIXED_PER_DAY,
			then: (schema) => schema.required('Fixed rate is required').min(0.01),
			otherwise: (schema) => schema.nullable(),
		})
		.label('Fixed rate'),
	threshold_km_per_day: Yup.number()
		.when('billing_type', {
			is: (val) =>
				val === BILLING_TYPE_FIXED ||
				val === BILLING_TYPE_FIXED_VARIABLE ||
				val === BILLING_TYPE_FIXED_PER_DAY,
			then: (schema) => schema.required('Threshold KM per day is required').min(0.01),
			otherwise: (schema) => schema.nullable(),
		})
		.label('Threshold KM Per Day'),
	threshold_km_per_month: Yup.number()
		.when('billing_type', {
			is: (val) =>
				val === BILLING_TYPE_FIXED ||
				val === BILLING_TYPE_FIXED_VARIABLE ||
				val === BILLING_TYPE_FIXED_PER_DAY,
			then: (schema) =>
				schema
					.required('Threshold KM per month is required')
					.min(0.01)
					.test(
						'is-greater-than-daily',
						'Threshold KM per month must be greater than threshold KM per day',
						function isGreaterThanDaily(value) {
							const { threshold_km_per_day } = this.parent;
							if (!threshold_km_per_day || !value) return true; // Skip if either is not set
							return value > threshold_km_per_day;
						}
					),
			otherwise: (schema) => schema.nullable(),
		})
		.label('Threshold KM Per Month'),
	// Cost fields are shown (and required) for Variable in both sub-types.
	vehicle_cost: Yup.number().required('Vehicle cost is required').min(0).label('Vehicle Cost'),
	loader_cost: Yup.number().required('Loader cost is required').min(0).label('Loader Cost'),
	delivery_associate_cost: Yup.number()
		.required('Delivery associate cost is required')
		.min(0)
		.label('Delivery Associate Cost'),
	// Fixed + Variable specific
	extra_charges_per_trip: Yup.number()
		.when('billing_type', {
			is: BILLING_TYPE_FIXED_VARIABLE,
			then: (schema) => schema.required('Extra charges per trip is required').min(0),
			otherwise: (schema) => schema.nullable(),
		})
		.label('Extra Charges per km'),
	extra_charges_per_hour: Yup.number()
		.when('billing_type', {
			is: BILLING_TYPE_FIXED_VARIABLE,
			then: (schema) => schema.required('Extra charges per hour is required').min(0),
			otherwise: (schema) => schema.nullable(),
		})
		.label('Extra Charges Per hour'),
	other_other_expenses: Yup.number()
		.nullable()
		.transform((value, originalValue) =>
			typeof originalValue === 'string' && originalValue.trim() === '' ? null : value
		)
		.when(['billing_type', 'other_expenses'], {
			is: (billingType, otherExpenses) =>
				(billingType === BILLING_TYPE_FIXED ||
					billingType === BILLING_TYPE_FIXED_VARIABLE ||
					billingType === BILLING_TYPE_VARIABLE ||
					billingType === BILLING_TYPE_FIXED_PER_DAY) &&
				otherExpenses !== null &&
				otherExpenses !== undefined &&
				String(otherExpenses).trim() !== '',
			then: (schema) =>
				schema
					.required('Other expenses (₹) is required when other expenses is specified')
					.min(0),
			otherwise: (schema) => schema.nullable(),
		})
		.label('Other Expenses (₹)'),
	// Variable specific
	charges_per_km: Yup.number().nullable().label('Charges per km'),
	// Fixed Per Day specific
	fixed_rate_per_day: Yup.number()
		.nullable()
		.when('billing_type', {
			is: BILLING_TYPE_FIXED_PER_DAY,
			then: (schema) =>
				schema
					.required('Fixed rate per day is required')
					.moreThan(0, 'Fixed rate per day must be greater than 0'),
			otherwise: (schema) => schema.nullable(),
		})
		.label('Fixed Rate Per Day'),
	// Sub Billing Type — required for Variable / Fixed + Variable / Fixed Per Day
	billing_subtype: Yup.string()
		.when('billing_type', {
			is: (val) =>
				val === BILLING_TYPE_VARIABLE ||
				val === BILLING_TYPE_FIXED_VARIABLE ||
				val === BILLING_TYPE_FIXED_PER_DAY,
			then: (schema) => schema.required('Sub billing type is required'),
			otherwise: (schema) => schema.nullable(),
		})
		.label('Sub Billing Type'),
	per_order_rates: Yup.array()
		.when('billing_subtype', {
			is: SUB_BILLING_PER_ORDER,
			then: (schema) =>
				schema
					.min(PER_ORDER_MIN_ROWS, `At least ${PER_ORDER_MIN_ROWS} rates are required`)
					.max(PER_ORDER_MAX_ROWS, `At most ${PER_ORDER_MAX_ROWS} rates are allowed`)
					.of(
						Yup.object({
							rate_id: Yup.string().required('Rate ID is required'),
							rate: Yup.number()
								.typeError('Rate must be a number')
								.required('Rate is required')
								.moreThan(0, 'Rate must be greater than 0'),
						})
					),
			otherwise: (schema) => schema.nullable(),
		})
		.label('Order Rates'),
	other_expenses: Yup.string()
		.when('billing_type', {
			is: (val) =>
				val === BILLING_TYPE_FIXED ||
				val === BILLING_TYPE_FIXED_VARIABLE ||
				val === BILLING_TYPE_VARIABLE,
			then: (schema) => schema.nullable(),
			otherwise: (schema) => schema.nullable(),
		})
		.label('Other Expenses'),
});
